(() => {
  'use strict';

  const qsa = (selector, scope = document) => [...scope.querySelectorAll(selector)];

  const initGallery = (root) => {
    if (!root || root.dataset.c67GalleryReady === 'true') return;
    root.dataset.c67GalleryReady = 'true';

    const filters = qsa('[data-c67-gallery-filter]', root);
    const items = qsa('[data-c67-gallery-item]', root);
    const grid = root.querySelector('[data-c67-gallery-grid]');
    const dialog = root.querySelector('[data-c67-gallery-dialog]');
    const dialogImage = root.querySelector('[data-c67-gallery-dialog-image]');
    const dialogCaption = root.querySelector('[data-c67-gallery-dialog-caption]');
    const previous = root.querySelector('[data-c67-gallery-prev]');
    const next = root.querySelector('[data-c67-gallery-next]');
    const archiveLayout = root.dataset.c67GalleryLayout === 'archive';
    let activeButton = null;
    let resizeTimer = null;

    const visibleItems = () => items.filter((item) => !item.hidden);
    const visibleButtons = () => visibleItems().map((item) => item.querySelector('[data-c67-gallery-open]')).filter(Boolean);

    const layoutArchive = () => {
      if (!archiveLayout || !grid) return;
      const styles = getComputedStyle(grid);
      const rowHeight = parseFloat(styles.gridAutoRows) || 8;
      const rowGap = parseFloat(styles.rowGap) || 10;
      visibleItems().forEach((item) => {
        item.style.gridRowEnd = 'auto';
        const content = item.querySelector('.c67-customer-gallery__image') || item;
        const height = content.getBoundingClientRect().height || content.scrollHeight;
        const span = Math.max(1, Math.ceil((height + rowGap) / (rowHeight + rowGap)));
        item.style.gridRowEnd = `span ${span}`;
      });
    };

    const scheduleLayout = () => requestAnimationFrame(() => requestAnimationFrame(layoutArchive));

    const updateNav = () => {
      const list = visibleButtons();
      const index = activeButton ? list.indexOf(activeButton) : -1;
      if (previous) previous.disabled = index <= 0;
      if (next) next.disabled = index < 0 || index >= list.length - 1;
    };

    const showButton = (button) => {
      if (!button || !dialog || !dialogImage) return;
      const source = button.querySelector('img');
      if (!source) return;
      activeButton = button;
      dialogImage.src = button.dataset.lightboxSrc || source.currentSrc || source.src;
      dialogImage.alt = source.alt || '';
      if (dialogCaption) dialogCaption.textContent = [button.dataset.title, button.dataset.detail].filter(Boolean).join(' · ');
      updateNav();
      if (!dialog.open && typeof dialog.showModal === 'function') dialog.showModal();
    };

    const step = (direction) => {
      const list = visibleButtons();
      const index = list.indexOf(activeButton);
      const target = list[index + direction];
      if (target) showButton(target);
    };

    filters.forEach((button) => {
      button.addEventListener('click', () => {
        const filter = button.dataset.c67GalleryFilter;
        filters.forEach((candidate) => {
          const active = candidate === button;
          candidate.classList.toggle('is-active', active);
          candidate.setAttribute('aria-pressed', active ? 'true' : 'false');
        });
        items.forEach((item) => { item.hidden = filter !== 'all' && item.dataset.room !== filter; });
        if (dialog?.open) dialog.close();
        scheduleLayout();
      });
    });

    qsa('[data-c67-gallery-open]', root).forEach((button) => {
      button.addEventListener('click', () => showButton(button));
      const image = button.querySelector('img');
      if (image && !image.complete) image.addEventListener('load', scheduleLayout, { once: true });
    });

    root.querySelector('[data-c67-gallery-close]')?.addEventListener('click', () => dialog?.close());
    previous?.addEventListener('click', () => step(-1));
    next?.addEventListener('click', () => step(1));
    dialog?.addEventListener('click', (event) => { if (event.target === dialog) dialog.close(); });
    dialog?.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowLeft') { event.preventDefault(); step(-1); }
      if (event.key === 'ArrowRight') { event.preventDefault(); step(1); }
    });
    dialog?.addEventListener('close', () => activeButton?.focus({ preventScroll: true }));

    if (archiveLayout) {
      window.addEventListener('resize', () => {
        clearTimeout(resizeTimer);
        resizeTimer = setTimeout(scheduleLayout, 120);
      }, { passive: true });
      scheduleLayout();
    }
  };

  const initFilmstrip = (section) => {
    if (!section || section.dataset.c67FilmstripReady === 'true') return;
    section.dataset.c67FilmstripReady = 'true';
    const rail = section.querySelector('[data-c67-project-rail]');
    if (!rail) return;
    const previous = section.querySelector('[data-c67-project-prev]');
    const next = section.querySelector('[data-c67-project-next]');
    const progress = section.querySelector('[data-c67-project-progress]');
    const scrollByPage = (direction) => rail.scrollBy({ left: direction * Math.max(rail.clientWidth * 0.82, 280), behavior: 'smooth' });
    const update = () => {
      const max = Math.max(0, rail.scrollWidth - rail.clientWidth);
      const ratio = max > 0 ? Math.min(1, Math.max(0, rail.scrollLeft / max)) : 1;
      if (progress) progress.style.setProperty('--c67-project-progress', `${Math.max(8, ratio * 100)}%`);
      if (previous) previous.disabled = rail.scrollLeft <= 2;
      if (next) next.disabled = rail.scrollLeft >= max - 2;
    };
    previous?.addEventListener('click', () => scrollByPage(-1));
    next?.addEventListener('click', () => scrollByPage(1));
    rail.addEventListener('scroll', update, { passive: true });
    update();
  };

  const initAll = (scope = document) => {
    qsa('[data-c67-customer-gallery]', scope).forEach(initGallery);
    qsa('[data-c67-projects]', scope).forEach(initFilmstrip);
    if (scope.matches?.('[data-c67-customer-gallery]')) initGallery(scope);
    if (scope.matches?.('[data-c67-projects]')) initFilmstrip(scope);
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initAll(document));
  else initAll(document);
  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
})();
